import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export type GradePricingValues = { A: number; B: number; C: number };
export const FALLBACK_GRADE_PRICING: GradePricingValues = { A: 75, B: 65, C: 50 };
export const GRADE_PRICING_KEY = "grade_pricing";
export const gradePricingQueryKey = (companyId: string | null | undefined) => ["grade-pricing", companyId ?? null];

function isValid(v: any): v is GradePricingValues {
  return v && typeof v === "object" && ["A", "B", "C"].every((k) => typeof v[k] === "number");
}

/** Resolves grade pricing: company override → platform default → hardcoded fallback. */
export function useGradePricing(companyId: string | null | undefined) {
  return useQuery({
    queryKey: gradePricingQueryKey(companyId),
    queryFn: async () => {
      const [platformRes, companyRes] = await Promise.all([
        supabase.from("platform_settings").select("setting_value").eq("setting_key", GRADE_PRICING_KEY).maybeSingle(),
        companyId
          ? supabase
              .from("company_settings")
              .select("setting_value")
              .eq("company_id", companyId)
              .eq("setting_key", GRADE_PRICING_KEY)
              .maybeSingle()
          : Promise.resolve({ data: null, error: null } as any),
      ]);
      const platformDefault = isValid(platformRes.data?.setting_value)
        ? (platformRes.data!.setting_value as unknown as GradePricingValues)
        : FALLBACK_GRADE_PRICING;
      const override = isValid(companyRes.data?.setting_value)
        ? (companyRes.data.setting_value as GradePricingValues)
        : null;
      return { platformDefault, override, effective: override ?? platformDefault, isOverride: !!override };
    },
  });
}
